<script setup>
import { ref, defineProps, defineModel, onMounted, watch, markRaw } from 'vue'
import IconPencil from '@/Components/Icons/IconPencil.vue'
import BaseIcon from '@/Components/Shared/BaseIcon.vue'
import iconMap from '@/Components/Icons/iconMap.js'

const props = defineProps({
    name: {
        type: String,
        required: false,
        default: '',
    },
    type: {
        type: String,
        required: true,
    },
    hasError: {
        type: Boolean,
        default: false,
    },
    required: {
        type: Boolean,
        default: false,
    },
    errorMessage: {
        type: String,
        default: '',
    },
    readonly: {
        type: Boolean,
        default: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    hasIcon: {
        type: Boolean,
        default: true,
    },
    iconName: {
        type: String,
        required: false,
        default: 'IconPencil',
    },
    placeholder: {
        type: String,
        default: '',
    },
    autocomplete: {
        type: String
    },
})

const DynamicIcon = ref(null)

watch(
    () => props.iconName,
    async (newIconName) => {
        if (newIconName && iconMap[newIconName]) {
            const component = await iconMap[newIconName]()
            DynamicIcon.value = markRaw(component.default)
        } else {
            DynamicIcon.value = null
        }
    },
    { immediate: true }
)

const model = defineModel()

const inputRef = ref(null)

const clear = () => {
    model.value = ''
}

const preventEnter = (event) => {
    if (event.key === 'Enter') {
        event.preventDefault()
    }
}

const preventArrowKeys = (event) => {
    if (props.type === 'number' && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
        event.preventDefault()
    }
}

const handleNumberInput = (event) => {
    if (props.type === 'number') {
        const value = event.target.value.replace(/[^\d]/g, '')
        model.value = value
        event.target.value = value
    }
}

const blockNonNumericKeys = (event) => {
    if (props.type === 'number') {
        const blockedKeys = ['e', 'E', '+', '-', '.', ',', ' ', 'ArrowUp', 'ArrowDown']

        if (blockedKeys.includes(event.key)) {
            event.preventDefault()
        }
    }
}

onMounted(() => {
    if (inputRef.value && inputRef.value.autofocus) {
        inputRef.value.focus()
    }
})
</script>
<template>
    <div :class="['mod-input-form']">
        <div>
            <div class="relative no-arrow">
                <input
                    ref="inputRef"
                    class="text"
                    :class="[
                        'rounded-lg block w-full p-4',
                        { 'm-error': hasError },
                        { 'm-readonly': readonly },
                        { 'm-disabled': disabled },
                        { isModelValue: modelValue },
                    ]"
                    :name="name"
                    :placeholder="placeholder"
                    :readonly="readonly || disabled"
                    :required="required"
                    :type="type"
                    :autocomplete="autocomplete"
                    @keydown.enter="preventEnter"
                    @keydown="blockNonNumericKeys"
                    @input="handleNumberInput"
                    @wheel.prevent
                    v-model="model"
                />
                <slot v-if="props.hasIcon" name="icon">
                    <div class="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5">
                        <base-icon v-if="!modelValue && !hasError" height="20px" :is-expanded="true" width="20px">
                            <Component
                                v-if="DynamicIcon && !model"
                                :is="DynamicIcon"
                                :color="
                                    hasError ? 'var(--app-color-ContentError)' : 'var(--app-color-ContentSecondary)'
                                "
                            />
                        </base-icon>
                        <svg
                            v-if="modelValue && !readonly && !disabled"
                            @click="clear"
                            :class="['cursor-pointer', { 'mr-3': hasError }]"
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M15 5.875L14.125 5L10 9.125L5.875 5L5 5.875L9.125 10L5 14.125L5.875 15L10 10.875L14.125 15L15 14.125L10.875 10L15 5.875Z"
                                :fill="hasError ? 'var(--app-color-ContentError)' : '#3A3A3A'"
                            />
                            <path
                                d="M10.8838 10L16.25 4.63381L15.3662 3.75L10 9.11619L4.63394 3.75L3.75 4.63381L9.11619 10L3.75 15.3662L4.63394 16.25L10 10.8838L15.3662 16.25L16.25 15.3662L10.8838 10Z"
                                :fill="hasError ? 'var(--app-color-ContentError)' : '#000000'"
                            />
                        </svg>
                        <svg
                            v-if="hasError && !modelValue"
                            fill="none"
                            height="20"
                            viewBox="0 0 20 20"
                            width="20"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M19.2817 9.65544L14.9292 2.20763C14.8705 2.10662 14.7865 2.02276 14.6853 1.96439C14.5841 1.90603 14.4695 1.87521 14.3527 1.875H5.6473C5.53051 1.87521 5.41582 1.90603 5.31466 1.96439C5.21351 2.02276 5.12942 2.10663 5.0708 2.20763L0.718234 9.65544C0.657176 9.76 0.625 9.87891 0.625 10C0.625 10.1211 0.657176 10.24 0.718234 10.3446L5.0708 17.7924C5.12942 17.8934 5.21351 17.9772 5.31466 18.0356C5.41582 18.094 5.53051 18.1248 5.6473 18.125H14.3527C14.4695 18.1248 14.5841 18.094 14.6853 18.0356C14.7865 17.9772 14.8705 17.8934 14.9292 17.7924L19.2817 10.3446C19.3428 10.24 19.375 10.1211 19.375 10C19.375 9.87891 19.3428 9.76 19.2817 9.65544ZM9.29686 5H10.7031V11.875H9.29686V5ZM9.99998 15.625C9.81456 15.625 9.63331 15.57 9.47914 15.467C9.32497 15.364 9.2048 15.2176 9.13385 15.0463C9.06289 14.875 9.04432 14.6865 9.0805 14.5046C9.11667 14.3227 9.20596 14.1557 9.33707 14.0246C9.46818 13.8935 9.63523 13.8042 9.81709 13.768C9.99894 13.7318 10.1874 13.7504 10.3587 13.8214C10.5301 13.8923 10.6765 14.0125 10.7795 14.1667C10.8825 14.3208 10.9375 14.5021 10.9375 14.6875C10.9375 14.9361 10.8387 15.1746 10.6629 15.3504C10.4871 15.5262 10.2486 15.625 9.99998 15.625Z"
                                fill="#D90B0B"
                            />
                        </svg>
                    </div>
                </slot>
            </div>
            <p v-if="errorMessage" class="relative font-medium text-sm text-red-600 dark:text-red-500 flex justify-end">
                <span class="absolute right-0">{{ errorMessage }}</span>
            </p>
        </div>
    </div>
</template>
<style scoped>
input {
    font-size: 14px;
    line-height: 25.35px;
    font-weight: 500;

    &[type="password"] {
        font: small-caption;
        font-size: 24px;
    }

    &:focus {
        border: none;
        box-shadow: none;
    }

    @apply h-12 bg-surface-secondary text-content-secondary border-0;

    &.m-error {
        @apply text-content-error;
        @apply border-2 border-content-error;

        &:focus {
            border: 2px solid #d90b0b;
            box-shadow: none !important;
            color: #000000;
            caret-color: #d90b0b;
            font-weight: 500;
            font-size: 14px;
            line-height: 25.35px;

            &[type="password"] {
                font: small-caption;
                font-size: 24px;
            }
        }
    }
}

.m-readonly {
    background-color: #dedede;
    cursor: not-allowed;
}

.isModelValue {
    color: #000;
}

.m-disabled {
    background-color: #dedede;
    color: #333333;
    cursor: not-allowed;
}

input::placeholder {
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
}

input:hover {
    cursor: pointer;
}

input:focus {
    cursor: text;
}

.no-arrow {
    input[type="number"] {
        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }

        -moz-appearance: textfield;
    }
}
</style>
